import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminVideoForm from './AdminVideoForm';
import AdminImagePreviewModal from './AdminImagePreviewModal';

const AdminVideoManager = () => {
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingItemId, setEditingItemId] = useState(null);
    const [isPublishing, setIsPublishing] = useState(null);

    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [previewImageUrl, setPreviewImageUrl] = useState('');

    const fetchItems = async () => {
        setIsLoading(true);
        const token = localStorage.getItem('app_token');
        const response = await axios.get('/api/admin/videos', { headers: { Authorization: `Bearer ${token}` } });
        setItems(response.data);
        setIsLoading(false);
    };

    useEffect(() => { fetchItems(); }, []);

    const handleSave = async (formData) => {
        const token = localStorage.getItem('app_token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const url = editingItemId ? `/api/admin/videos/${editingItemId}` : '/api/admin/videos';
        const method = editingItemId ? 'put' : 'post';
        await axios[method](url, formData, config);
        fetchItems();
        setIsFormOpen(false);
        setEditingItemId(null);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this video?')) {
            const token = localStorage.getItem('app_token');
            await axios.delete(`/api/admin/videos/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            fetchItems();
        }
    };
    
    const handleStatusToggle = async (id, currentStatus) => {
        try {
            const token = localStorage.getItem('app_token');
            const newStatus = !currentStatus;
            await axios.put(`/api/admin/videos/${id}/status`, 
                { isActive: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setItems(items.map(item => 
                item.id === id ? { ...item, is_active: newStatus } : item
            ));
        } catch (err) {
            alert('Failed to update status.');
        }
    };

    const handleOpenPreview = (imageUrl) => {
        setPreviewImageUrl(imageUrl);
        setIsPreviewModalOpen(true);
    };

    const handleClosePreview = () => {
        setIsPreviewModalOpen(false);
        setPreviewImageUrl('');
    };

    const handlePublishToYouTube = async (id) => {
        if (!window.confirm('This will upload the video to your YouTube channel as "Unlisted". Are you sure you want to proceed?')) return;
        
        setIsPublishing(id);
        try {
            const token = localStorage.getItem('app_token');
            const response = await axios.post(`/api/videos/${id}/publish-youtube`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message || 'Successfully started upload to YouTube! It may take a few minutes to appear on your channel.');
        } catch (error) {
            alert(error.response?.data?.error || 'An unexpected error occurred while publishing.');
        } finally {
            setIsPublishing(null);
        }
    };
    
    if (isLoading) return <p>Loading videos...</p>;

    return (
        <div className="admin-content-manager">
            {isPreviewModalOpen && (
                <AdminImagePreviewModal imageUrl={previewImageUrl} onCancel={handleClosePreview} />
            )}
            {isFormOpen && <AdminVideoForm videoId={editingItemId} onSave={handleSave} onCancel={() => { setIsFormOpen(false); setEditingItemId(null); }} />}
            
            <div className="admin-content-header">
                <h3 className="admin-section-title">Manage Videos</h3>
                <button onClick={() => { setEditingItemId(null); setIsFormOpen(true); }} className="btn-primary">Add New Video</button>
            </div>

            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Thumbnail</th>
                            <th>Title</th>
                            <th>Status</th>
                            <th>Category</th>
                            <th>Author</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(item => (
                            <tr key={item.id} style={{ opacity: item.is_active ? 1 : 0.5 }}>
                                <td>{item.id}</td>
                                <td>
                                    <div onClick={() => handleOpenPreview(item.thumbnail_url)} style={{ cursor: 'pointer' }}>
                                        <img 
                                          src={item.thumbnail_url || 'https://placehold.co/300x180?text=No+Image'}
                                          alt="Thumbnail" 
                                          className="admin-table-thumbnail"
                                          onError={e => { e.target.onerror = null; e.target.src = 'https://placehold.co/300x180?text=No+Image'; }}
                                        />
                                    </div>
                                </td>
                                <td>
                                    <Link to={`/videos/${item.id}`} target="_blank" rel="noopener noreferrer">
                                        {item.title}
                                    </Link>
                                </td>
                                <td>
                                    <div className="status-cell">
                                        <span className={`status-badge ${item.is_active ? 'active' : 'inactive'}`}>
                                            {item.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                        <label className="toggle-switch">
                                            <input type="checkbox" checked={item.is_active} onChange={() => handleStatusToggle(item.id, item.is_active)} />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>
                                </td>
                                <td>{item.category}</td>
                                <td>{item.author_name}</td>
                                <td className="admin-table-actions">
                                    <button onClick={() => handlePublishToYouTube(item.id)} className="action-btn" title="Publish to YouTube" disabled={isPublishing === item.id}>
                                        <span className="material-icons" style={{color: '#FF0000'}}>{isPublishing === item.id ? 'hourglass_top' : 'smart_display'}</span>
                                    </button>
                                    <button onClick={() => { setEditingItemId(item.id); setIsFormOpen(true); }} className="action-btn edit" title="Edit"><span className="material-icons">edit</span></button>
                                    <button onClick={() => handleDelete(item.id)} className="action-btn delete" title="Delete"><span className="material-icons">delete_forever</span></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminVideoManager;